import React, { useState, useContext, useEffect } from "react"
import { Link } from "react-router-dom"
import gql from "graphql-tag"
import { IoMdCheckmarkCircleOutline } from "react-icons/io"
import { RxCross1 } from "react-icons/rx"
import { HiOutlineMail } from "react-icons/hi"
import { BiUser } from "react-icons/bi"
import TimeDelayedModal from "../TimeDelayedModal"
import { Mutation } from "react-apollo"
import validator from "validator"
import AppContext from "../../shared/AppContext"
import { useForm } from "react-hook-form"

import "./freeTrialModal.scss"

const ARTICLE_FREE_TRIAL = gql`
  mutation ARTICLE_FREE_TRIAL(
    $email: String!
    $firstName: String!
    $lastName: String!
    $industry: industryType!
  ) {
    freeTrialRequest(
      email: $email
      lastName: $lastName
      firstName: $firstName
      industry: $industry
    ) {
      payload
      message
    }
  }
`

const FreeTrialModalContent = (props) => {
  const { onClose, mutate, viewer } = props
  const [isSuccess, setSuccess] = useState(false)
  const [error, setError] = useState("")
  const { trackEvent } = useContext(AppContext)

  const getDefaultValues = () => {
    if (viewer.isUserAuthenticated) {
      const name = viewer.name.split(" ")
      const lastName = name.pop()
      const firstName = name.join(" ")
      return {
        firstName: firstName,
        lastName: lastName,
        email: viewer.email,
        industry: "TECHNOLOGY",
      }
    }

    return {
      firstName: "",
      lastName: "",
      email: "",
      industry: "TECHNOLOGY",
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultValues(),
  })

  useEffect(() => {
    trackEvent("Free Trial Presented", {
      "Form Section": "Articles",
    })
  }, [])

  const onSubmit = async (data) => {
    const res = await mutate({
      variables: {
        ...data,
      },
    })

    if (
      res &&
      res.data &&
      res.data.freeTrialRequest &&
      res.data.freeTrialRequest.payload === "success"
    ) {
      setSuccess(true)
      setError("")

      trackEvent("Free Trial Submitted", {
        "Form Section": "Articles",
        "First Name": data.firstName,
        "Last Name": data.lastName,
        Email: data.email,
        Industry: data.industry,
      })

      return
    }

    if (!res || !res.data || !res.data.freeTrialRequest) {
      setSuccess(false)
      setError("An error has occurred.")

      trackEvent("Free Trial Submit Error", {
        "Form Section": "Articles",
        "First Name": data.firstName,
        "Last Name": data.lastName,
        Email: data.email,
        Industry: data.industry,
        "Error Message": "An error has occurred.",
      })

      return
    }

    if (res.data.freeTrialRequest.payload !== "success") {
      setSuccess(false)
      setError(res.data.freeTrialRequest.message)

      trackEvent("Free Trial Submit Error", {
        "Form Section": "Articles",
        "First Name": data.firstName,
        "Last Name": data.lastName,
        Email: data.email,
        Industry: data.industry,
        "Error Payload": res.data.freeTrialRequest.payload,
        "Error Message": res.data.freeTrialRequest.message,
      })

      return
    }

    return
  }

  const renderRequestForm = () => {
    return (
      <div className="request-form">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-field">
            <label htmlFor="firstName">First Name</label>
            <div className="input">
              <input
                id="firstName"
                data-cy="first-name-input"
                name="firstName"
                style={
                  errors.firstName
                    ? { border: "1px red solid" }
                    : { border: "1px solid #E2E8F0" }
                }
                ref={register({ required: true })}
              />
              <BiUser />
              {errors.firstName && (
                <span className="error" data-cy="firstname-error-message">
                  Required field.
                </span>
              )}
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="lastName">Last Name</label>
            <div className="input">
              <input
                id="lastName"
                data-cy="last-name-input"
                style={
                  errors.lastName
                    ? { border: "1px red solid" }
                    : { border: "1px solid #E2E8F0" }
                }
                name="lastName"
                ref={register({ required: true })}
              />
              <BiUser />
              {errors.lastName && (
                <span className="error" data-cy="lastname-error-message">
                  Required field.
                </span>
              )}
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <div className="input">
              <input
                data-cy="email-input"
                id="email"
                name="email"
                style={
                  errors.email
                    ? { border: "1px red solid" }
                    : { border: "1px solid #E2E8F0" }
                }
                ref={register({ required: true, validate: validator.isEmail })}
              />
              <HiOutlineMail />
              {errors.email && (
                <span className="error" data-cy="email-error-message">
                  Please enter a valid email.
                </span>
              )}
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="industry">Industry</label>

            <select
              id="industry"
              data-cy="industry-input"
              name="industry"
              ref={register}
            >
              <option value="TECHNOLOGY">Enterprise Technology</option>
              <option value="FINANCIAL">Financial Services</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div className="tos">
            <span>By submitting, you are agreeing to our </span>
            <span
              data-cy="terms-of-use"
              onClick={() => {
                trackEvent("Free Trial Terms Of Use", {
                  "Form Section": "Articles",
                })
              }}
            >
              <a
                href="https://etr.ai/term-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              .
            </span>
          </div>
          <button
            data-cy="free-trial-submit"
            className="blue-button"
            type="submit"
          >
            Request your free trial
          </button>
        </form>
        <div className="skip">
          <span
            data-cy="skip-free-trial"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault()
              trackEvent("Free Trial Skip")
              onClose()
            }}
          >
            Skip free trial
          </span>
        </div>
      </div>
    )
  }

  const renderSuccess = () => {
    return (
      <div className="success" data-cy="success">
        <p className="title">Success</p>
        <p className="message">Thank you for signing up for our free trial!</p>

        <button className="blue-button" onClick={onClose}>
          Close
        </button>
      </div>
    )
  }

  const renderForm = () => {
    return (
      <div className="free-trial-modal-form">
        <div className="top">
          <p>Start Your Free Trial</p>
          <button
            data-cy="x-close"
            className="close"
            onClick={(e) => {
              e.preventDefault()
              trackEvent("Free Trial Modal Dismissed", {
                "Form Section": "Articles",
              })
              onClose()
            }}
          >
            <RxCross1 />
          </button>
        </div>

        {isSuccess ? renderSuccess() : renderRequestForm()}
      </div>
    )
  }

  return (
    <div className="free-trial-modal-content">
      <div
        className="free-trial-modal-info"
        style={{
          backgroundImage: `url("/static/images/free-trial-bg.png")`,
          backgroundSize: "cover",
        }}
      >
        <div className="header">
          <img src="/static/images/ETR_only_white.png"></img>
          <p>Experience the Value of the ETR Platform with a Free Trial</p>
        </div>

        <ul>
          <li>
            <IoMdCheckmarkCircleOutline className="list-icon" />
            <span className="list-text">Access IT spend survey data</span>
          </li>
          <li>
            <IoMdCheckmarkCircleOutline className="list-icon" />
            <span className="list-text">
              Access the research report library
            </span>
          </li>
          <li>
            <IoMdCheckmarkCircleOutline className="list-icon" />
            <span className="list-text">
              Analyze data utilizing various models
            </span>
          </li>
          <li>
            <IoMdCheckmarkCircleOutline className="list-icon" />
            <span className="list-text">
              Discover insights through discussions and interviews
            </span>
          </li>
        </ul>
      </div>
      {renderForm()}
    </div>
  )
}

const FreeTrialModal = (props) => {
  const { delay, data, viewer } = props

  const showModal =
    (!viewer && data.page.isPublic) || !viewer.roles || !viewer.roles.length

  if (showModal) {
    return (
      <Mutation mutation={ARTICLE_FREE_TRIAL}>
        {(mutate) => {
          return (
            <TimeDelayedModal delayInSeconds={delay} modalWidth={"750px"}>
              <FreeTrialModalContent mutate={mutate} viewer={viewer} />
            </TimeDelayedModal>
          )
        }}
      </Mutation>
    )
  }

  return null
}

export default FreeTrialModal
